/**
 * modal-layers.ts — the portal's open dialogs, what is made inert under them,
 * and what stays live over them.
 *
 * A dialog is modal by the inert around it: while one is open the app
 * (#root) is inert, and so is every dialog but the one drawn on top — no
 * click, no Tab, no screen reader reaches what is under it, as WAI-ARIA asks
 * of a modal ("authors SHOULD mark all other contents as inert") and the HTML
 * standard says of inert ("user agents do not expose the inert nodes to
 * accessibility APIs or assistive technologies"). Modal sets no aria-modal:
 * where the platform maps it — Apple's accessibility API, which VoiceOver reads
 * in Safari and in Chrome, and Android's — aria-modal="true" prunes everything
 * outside the dialog holding the focus from the accessibility tree, and the
 * toasts went with it, so a toast raised while a dialog was open went unheard
 * under VoiceOver. The toasts stand outside the app (toast-viewport.tsx), so
 * they stay live. The game client does the same
 * (game-client/src/components/ui/modal-layers.ts), by the same step
 * (inertUnderTop, in the mirrored use-modal-behavior.ts).
 *
 * Set at once, not on a render: focus going back into the app as the last
 * dialog closes needs it live already.
 *
 * What must stay live while a dialog is open renders beside the dialogs,
 * above the app (abovePage): the toasts, and the moderator's call windows over
 * a player's or a team's card. Left in the app, they went inert with it.
 */

import type { ReactNode, ReactPortal } from 'react';
import { createPortal } from 'react-dom';

import { inertUnderTop, rungOf } from './use-modal-behavior';

/** The open dialogs, in the order they opened, each with the rung it is drawn on and its overlay. */
const layers: Array<{ id: symbol; rung: number; element: HTMLElement | null }> = [];

/** Every dialog but the one drawn on top is inert, and the app is while any is open. */
function settle() {
  inertUnderTop(layers);
  document.getElementById('root')?.toggleAttribute('inert', layers.length > 0);
}

/**
 * A dialog open on its rung — its overlay's z-index — until the returned
 * function closes it: what it covers inert from now, and live again as it
 * closes.
 */
export function openLayer(element: HTMLElement | null): () => void {
  const id = Symbol('dialog');
  const rung = element ? rungOf(element) || 0 : 0;
  layers.push({ id, rung, element });
  settle();
  return () => {
    const index = layers.findIndex((layer) => layer.id === id);
    if (index !== -1) layers.splice(index, 1);
    settle();
  };
}

/**
 * Rendered into the body beside the dialogs, above the app: live while a
 * dialog is open, as the app is inert then and nothing here is one of the
 * dialogs this list makes inert. The toasts render so, and FloatingWindow
 * (floating-window.tsx) — the moderator's private call and voice monitor — on
 * a rung of its own over every dialog's scrim (--z-live-window): their mute
 * and their close are the moderator's while the card they were opened from
 * stays open. Such a window is none of the dialogs: no dialog is made inert
 * under it, and it takes neither the focus nor the keys from the one on top —
 * it takes no focus as it opens, and Escape closes the dialog, as beside the
 * game client's call window, which stands the same way (FloatingWindow's
 * abovePage, page-layers.tsx there).
 */
export function abovePage(children: ReactNode): ReactPortal {
  return createPortal(children, document.body);
}
